import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { RoleService } from '../../services/roleService';
import { getCurrentMillis } from '../../utils/dateUtils';
import { X, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import type { Office, Role } from '../../types';

interface EditOfficeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedOffice: Office) => void;
  office: Office | null;
  role: Role | null;
  isLoading?: boolean;
}

const EditOfficeModal: React.FC<EditOfficeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  office,
  role,
  isLoading = false
}) => {
  const { strings } = useLocalization();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    subSpecialityID: '',
    lawyerTitleID: '',
    salaryIndicator: 0,
    fixedSalaryAmount: 0,
    percentageAmount: 0,
    profitPercentage: 0,
    isOwner: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load form data when modal opens
  useEffect(() => {
    if (isOpen && role) {
      setFormData({
        subSpecialityID: role.subSpecialityID || '',
        lawyerTitleID: role.lawyerTitleID || '',
        salaryIndicator: role.salaryIndicator || 0,
        fixedSalaryAmount: (role.salaryIndicator === 1 || role.salaryIndicator === 3) ? (role.salaryValue || 0) : 0,
        percentageAmount: (role.salaryIndicator === 2 || role.salaryIndicator === 3) ? (role.salaryValue || 0) : 0,
        profitPercentage: role.profitPercentage || 0,
        isOwner: role.isOfficeCreator || false
      });
      setErrors({});
    }
  }, [isOpen, role]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.salaryIndicator === 1 && formData.fixedSalaryAmount <= 0) {
      newErrors.fixedSalaryAmount = strings.myOffices.salaryValueRequired || 'Fixed salary amount is required';
    }

    if (formData.salaryIndicator === 2 && formData.percentageAmount <= 0) {
      newErrors.percentageAmount = strings.myOffices.salaryValueRequired || 'Percentage amount is required';
    }

    if (formData.salaryIndicator === 3 && formData.fixedSalaryAmount <= 0 && formData.percentageAmount <= 0) {
      newErrors.fixedSalaryAmount = strings.myOffices.salaryValueRequired || 'At least one amount is required for fixed + percentage salary';
      newErrors.percentageAmount = strings.myOffices.salaryValueRequired || 'At least one amount is required for fixed + percentage salary';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!office || !role || !user) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Map the separate fields back to salaryValue based on salary type
      let salaryValue = 0;
      if (formData.salaryIndicator === 1) {
        salaryValue = formData.fixedSalaryAmount;
      } else if (formData.salaryIndicator === 2) {
        salaryValue = formData.percentageAmount;
      } else if (formData.salaryIndicator === 3) {
        // For fixed + percentage, use the fixed amount as the primary value
        salaryValue = formData.fixedSalaryAmount;
      }

      // Update the role data
      const updatedRoleData = {
        subSpecialityID: formData.subSpecialityID,
        lawyerTitleID: formData.lawyerTitleID,
        salaryIndicator: formData.salaryIndicator,
        salaryValue: salaryValue,
        profitPercentage: formData.profitPercentage,
        isOwner: formData.isOwner,
        updatedAt: getCurrentMillis()
      };

      await RoleService.updateRole(role.id, updatedRoleData, user.id);

      // Create updated office object for callback
      const updatedOffice = {
        ...office,
        updatedAt: getCurrentMillis()
      };

      onSave(updatedOffice);
      onClose();
    } catch (error) {
      console.error('Error updating office:', error);
      setErrors({ submit: strings.myOffices.updateError || 'Failed to update office. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !office || !role) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            {strings.myOffices.editOffice || 'Edit Office'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Office Info (Read-only) */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-3">
              {strings.myOffices.officeInformation}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.officeName}</p>
                <p className="text-slate-800 dark:text-white">{office.name}</p>
              </div>
              <div>
                <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.officeAddress}</p>
                <p className="text-slate-800 dark:text-white">{office.address || strings.myOffices.noDataAvailable}</p>
              </div>
            </div>
          </div>

          {/* Role Information (Editable) */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
              {strings.myOffices.roleDetails}
            </h3>

            {/* Specialty */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {strings.myOffices.specialty}
              </label>
              <select
                value={formData.subSpecialityID}
                onChange={(e) => handleInputChange('subSpecialityID', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{strings.myOffices.generalPractice}</option>
                {/* Add specialty options here */}
              </select>
            </div>

            {/* Lawyer Title */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {strings.myOffices.lawyerTitle || 'Lawyer Title'}
              </label>
              <select
                value={formData.lawyerTitleID}
                onChange={(e) => handleInputChange('lawyerTitleID', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{strings.myOffices.legalProfessional}</option>
                {/* Add lawyer title options here */}
              </select>
            </div>

            {/* Salary Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {strings.myOffices.salaryType}
              </label>
              <select
                value={formData.salaryIndicator}
                onChange={(e) => handleInputChange('salaryIndicator', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value={0}>{strings.myOffices.studentIntern}</option>
                <option value={1}>{strings.myOffices.fixedSalary}</option>
                <option value={2}>{strings.myOffices.percentageBased}</option>
                <option value={3}>{strings.myOffices.fixedPercentage}</option>
              </select>
            </div>

            {/* Fixed Salary Value (for fixed salary and fixed + percentage) */}
            {(formData.salaryIndicator === 1 || formData.salaryIndicator === 3) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {strings.myOffices.fixedSalaryAmount || 'Fixed Salary Amount'}
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.fixedSalaryAmount}
                    onChange={(e) => handleInputChange('fixedSalaryAmount', parseFloat(e.target.value) || 0)}
                    className={`flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.fixedSalaryAmount ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400">EGP</span>
                </div>
                {errors.fixedSalaryAmount && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.fixedSalaryAmount}</p>
                )}
              </div>
            )}

            {/* Percentage Value (for percentage based and fixed + percentage) */}
            {(formData.salaryIndicator === 2 || formData.salaryIndicator === 3) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {strings.myOffices.percentageAmount || 'Percentage Amount'}
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.percentageAmount}
                    onChange={(e) => handleInputChange('percentageAmount', parseFloat(e.target.value) || 0)}
                    className={`flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.percentageAmount ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400">%</span>
                </div>
                {errors.percentageAmount && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.percentageAmount}</p>
                )}
              </div>
            )}

            {/* Profit Percentage (always shown) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {strings.myOffices.profitPercentage}
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.profitPercentage}
                  onChange={(e) => handleInputChange('profitPercentage', parseFloat(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0"
                />
                <span className="text-sm text-slate-500 dark:text-slate-400">%</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Button
              label={strings.common.cancel}
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Button
              label={isSubmitting ? (strings.myOffices.updating || 'Updating...') : (strings.myOffices.update || 'Update')}
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOfficeModal;
